import type { Agent } from "@cc/protocol";
import { BOT_PROFILES, createBotAgent } from "@cc/agents/heuristic";
import { createCricketAgent } from "@cc/agents/browser";
import type { CricketBrainConfig } from "@cc/agents/browser";

/**
 * The crickets available in the browser build.
 *
 * Two kinds, deliberately kept in one list so the arena treats them identically:
 *
 *  - **Built-in heuristics.** Always available, no setup. Someone opening the
 *    site should see a real match immediately rather than a configuration
 *    screen.
 *  - **Live AI crickets.** Real models, wired up per match from the duel modal.
 *    Up to two can be fielded at once (AI vs AI), each with its own key, model
 *    and ring name. Nothing is stored: no account, no login, no saved roster.
 *
 * Keys live in memory for the session only — see `aiConfigs` below — which is
 * the honest posture for a client-only project. There is no backend to hold a
 * credential, and the UI says so instead of implying a guarantee we cannot make.
 */

export interface BrowserCricket {
  readonly id: string;
  /** Canonical name shown to spectators. Also the engine persona. */
  readonly persona: string;
  readonly model: string;
  /** Which copy to use for the roster description. */
  readonly blurbKey: "collector" | "scout" | "hunter" | "ai";
  /**
   * Ball mark: image URL (`/icons/…`) or a short glyph/emoji.
   * Omitted for built-in bots — the renderer falls back to slot letters / bot glyphs.
   */
  readonly icon?: string;
  readonly create: () => Agent;
}

export const CRICKETS: readonly BrowserCricket[] = [
  {
    id: "collector",
    persona: "THE COLLECTOR",
    model: "heuristic",
    blurbKey: "collector",
    create: () => createBotAgent({ ...BOT_PROFILES.collector, id: "collector" }),
  },
  {
    id: "scout",
    persona: "THE SCOUT",
    model: "heuristic",
    blurbKey: "scout",
    create: () => createBotAgent({ ...BOT_PROFILES.scout, id: "scout" }),
  },
  {
    id: "hunter",
    persona: "THE HUNTER",
    model: "heuristic",
    blurbKey: "hunter",
    create: () => createBotAgent({ ...BOT_PROFILES.hunter, id: "hunter" }),
  },
];

/**
 * Reserved ids for the two live AI cricket slots.
 *
 * A match needs unique agent ids, so AI vs AI cannot share a single `"ai"` id.
 * Legacy `"ai"` in share links is treated as `ai-a`.
 */
export const AI_CRICKET_A_ID = "ai-a";
export const AI_CRICKET_B_ID = "ai-b";
/** @deprecated Prefer AI_CRICKET_A_ID; kept for older share links. */
export const AI_CRICKET_ID = AI_CRICKET_A_ID;

export const AI_CRICKET_IDS = [AI_CRICKET_A_ID, AI_CRICKET_B_ID] as const;
export type AiCricketId = (typeof AI_CRICKET_IDS)[number];

/**
 * Everything needed to run one AI cricket for one session.
 *
 * `apiKey` is the sensitive field and is the reason this object is never
 * serialised. See `setAiConfig`.
 */
export interface AiConfig {
  readonly providerId: string;
  readonly baseUrl: string;
  readonly model: string;
  readonly apiKey: string;
  readonly name: string;
  /**
   * Ball icon: provider logo URL or a user-picked emoji.
   * Empty / omitted means "use the provider preset's default logo".
   */
  readonly icon?: string;
}

/**
 * Session-only AI configurations, keyed by cricket id (`ai-a` / `ai-b`).
 *
 * A module-level map rather than `localStorage`, which is the whole point: keys
 * exist only while this tab is open. No login, no persistence, nothing for a
 * shared machine to leak, and nothing to clean up server-side because there is
 * no server.
 */
const aiConfigs = new Map<string, AiConfig>();

/** Normalises legacy `"ai"` share-link ids onto the A slot. */
export function resolveAiId(id: string): string {
  return id === "ai" ? AI_CRICKET_A_ID : id;
}

export function isAiCricketId(id: string): boolean {
  const resolved = resolveAiId(id);
  return resolved === AI_CRICKET_A_ID || resolved === AI_CRICKET_B_ID;
}

export function setAiConfig(id: AiCricketId | "ai", config: AiConfig | null): void;
/** Clears every AI slot. */
export function setAiConfig(config: null): void;
export function setAiConfig(
  idOrNull: AiCricketId | "ai" | null,
  config?: AiConfig | null,
): void {
  if (idOrNull === null && config === undefined) {
    aiConfigs.clear();
    return;
  }
  const id = resolveAiId(idOrNull as string);
  if (config == null) {
    aiConfigs.delete(id);
    return;
  }
  aiConfigs.set(id, config);
}

export function getAiConfig(id: AiCricketId | "ai" = AI_CRICKET_A_ID): AiConfig | null {
  return aiConfigs.get(resolveAiId(id)) ?? null;
}

export function hasAiConfig(id?: AiCricketId | "ai"): boolean {
  if (id !== undefined) {
    const config = getAiConfig(id);
    return config !== null && config.apiKey.trim() !== "" && config.model.trim() !== "";
  }
  return AI_CRICKET_IDS.some((slot) => hasAiConfig(slot));
}

/**
 * The AI cricket for a given slot, if configured.
 *
 * `create()` throws `MissingKeyError` rather than falling back to a bot when the
 * key is absent. A silent downgrade would make the result a lie: the viewer would
 * believe they had watched a model play when they had watched a heuristic.
 */
export function aiCricket(id: AiCricketId, config: AiConfig): BrowserCricket {
  const name = config.name.trim() || "AI CRICKET";
  const icon = config.icon?.trim() || undefined;
  return {
    id,
    persona: name,
    model: config.model,
    blurbKey: "ai",
    ...(icon ? { icon } : {}),
    create: () => {
      const brain: CricketBrainConfig = {
        id,
        persona: name,
        providerId: config.providerId,
        baseUrl: config.baseUrl,
        model: config.model,
        apiKey: config.apiKey,
      };
      return createCricketAgent(brain);
    },
  };
}

/**
 * Resolves any cricket id to a runnable cricket.
 *
 * AI crickets are resolved from the live session config, so an id alone is not
 * enough to rebuild them — which is correct and worth stating: a replay link for
 * a match involving an AI reproduces the *match*, not the model's decisions. The
 * engine can replay the recorded action log exactly, but a model asked the same
 * question twice may answer differently, so a model match is reproducible in
 * outcomes but not bit-for-bit. Heuristic matches are bit-for-bit exact.
 */
export function findCricket(id: string): BrowserCricket {
  const resolved = resolveAiId(id);
  if (isAiCricketId(resolved)) {
    const config = getAiConfig(resolved as AiCricketId);
    if (!config) throw new Error("The AI cricket is not configured in this session.");
    return aiCricket(resolved as AiCricketId, config);
  }
  const found = CRICKETS.find((c) => c.id === id);
  if (!found) throw new Error(`Unknown cricket "${id}"`);
  return found;
}

/** Every cricket currently selectable, AI slots last. */
export function selectableCrickets(): BrowserCricket[] {
  // Gate on `hasAiConfig(id)` rather than the raw config: a draft with a blank
  // key or model is not a runnable cricket, and listing it would let a viewer
  // select something that throws the moment the match starts.
  const list: BrowserCricket[] = [...CRICKETS];
  for (const id of AI_CRICKET_IDS) {
    if (!hasAiConfig(id)) continue;
    list.push(aiCricket(id, getAiConfig(id)!));
  }
  return list;
}

/** Is this a cricket id the arena can attempt to run? */
export function isKnownCricketId(id: string): boolean {
  const resolved = resolveAiId(id);
  if (isAiCricketId(resolved)) return hasAiConfig(resolved as AiCricketId);
  return CRICKETS.some((c) => c.id === id);
}

/** Next free AI slot id, preferring A then B. */
export function nextAiSlot(occupied: readonly string[] = []): AiCricketId | null {
  const taken = new Set(occupied.map(resolveAiId));
  for (const id of AI_CRICKET_IDS) {
    if (!taken.has(id)) return id;
  }
  return null;
}
